import React, { useState } from 'react'
import clsx from 'clsx'
import Tooltip from 'components/Tooltip'
import { NavLink, useLocation } from 'react-router-dom'
import { Typography, makeStyles, styled } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { ExpandLess, ExpandMore } from '@material-ui/icons'

const useStyles = ({ sidebarOpen }) =>
  makeStyles(theme => ({
    navItem: {
      'display': 'flex',
      'flex': 1,
      'flexDirection': 'row',
      'alignItems': 'center',
      'justifyContent': sidebarOpen ? 'flex-start' : 'center',
      'borderRadius': theme.spacing(1),
      'textDecoration': 'none',
      '&:hover': {
        backgroundColor: theme.palette.grey.F4,
      },
    },
    navItemActive: {
      backgroundColor: theme.palette.grey.F4,
    },
  }))

const NavContainer = styled('div')(({ sidebarOpen, hasSubmenu, theme, isItemActive }) => ({
  backgroundColor:
    hasSubmenu && !sidebarOpen && isItemActive ? theme.palette.grey.F4 : 'transparent',
  margin: !sidebarOpen ? theme.spacing(1, 2) : theme.spacing(1, 1),
  borderRadius: theme.spacing(1),
}))

const ItemIcon = styled(Typography)(({ theme, isActive }) => ({
  height: 40,
  minWidth: 40,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: theme.spacing(1),
  color: isActive ? theme.palette.blue.main : theme.palette.primary.main,
}))

const ItemText = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  fontWeight: 400,
  marginLeft: theme.spacing(0),
  color: theme.palette.primary.main,
  display: 'flex',
  flex: 1,
}))

const SubMenuIndicator = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
}))

const SubMenuContainer = styled('div')(({ sidebarOpen }) => ({
  display: 'flex',
  flexDirection: 'column',
  marginLeft: sidebarOpen ? 30 : 0,
}))

export const NavItem = ({ item, sidebarOpen }) => {
  const { t } = useTranslation()
  const location = useLocation()
  const classes = useStyles({ sidebarOpen })()

  const [iSubMenuOpen, setIsSubmenuOpen] = useState(false)

  const isAnySubMenuActive = item?.subMenus?.some(sub => {
    return location.pathname.includes(sub.to)
  })

  const isItemActive = location.pathname === item.to || isAnySubMenuActive

  const handleClick = event => {
    if (!item.subMenus) return

    event.preventDefault()
    setIsSubmenuOpen(prev => !prev)
  }

  return (
    <NavContainer
      hasSubmenu={!!item?.subMenus}
      sidebarOpen={sidebarOpen}
      isItemActive={isItemActive}>
      <NavLink
        className={clsx(classes.navItem, isItemActive && classes.navItemActive)}
        to={item.to ?? '#'}
        onClick={handleClick}>
        <Tooltip title={t(item.label)}>
          <ItemIcon isActive={isItemActive}>{isItemActive ? item.activeIcon : item.icon}</ItemIcon>
        </Tooltip>

        {sidebarOpen && <ItemText>{t(item.label)}</ItemText>}

        {item.subMenus && sidebarOpen && (
          <SubMenuIndicator>{iSubMenuOpen ? <ExpandLess /> : <ExpandMore />}</SubMenuIndicator>
        )}
      </NavLink>

      {iSubMenuOpen && item.subMenus?.length > 0 && (
        <SubMenuContainer sidebarOpen={sidebarOpen}>
          {item.subMenus.map(subMenu => (
            <NavItem key={subMenu.to} item={subMenu} sidebarOpen={sidebarOpen} />
          ))}
        </SubMenuContainer>
      )}
    </NavContainer>
  )
}
